import React, {FC, memo, useEffect, useState} from "react";
import {InfoCircleTwoTone} from "@ant-design/icons";
import {Tag, Modal, Switch, Space, Form} from "antd";
import {FormikProps} from "formik";
import css from "./GeneratedNumberBadge.less";
import {colorPrimaryBackground} from "../../styles/colors";
import i18n from "../../services/i18n";
import {CourseNumberInput} from "../../containers/courses/CourseViews/CourseStepViews";
import {PossibleFormikPropsType} from "../../containers/courses/StepCourseData";
import {
    CourseNumberFormat,
    useGetCourseNumberFormatQuery,
} from "../../generated/graphql";

type GeneratedNumberBadgeProps = {
    number?: string;
    type?: "courseNumber" | "customerNumber";
    modalTitle?: React.ReactNode;
    modalContent?: React.ReactNode;
    formikProps: FormikProps<PossibleFormikPropsType>;
    style?: React.CSSProperties;
};

const openModal = (title?: React.ReactNode, content?: React.ReactNode) => {
    if (title !== undefined || content !== undefined) {
        Modal.info({title, content});
    }
};

const GeneratedNumberBadge: FC<GeneratedNumberBadgeProps> = ({
    number,
    modalTitle,
    modalContent,
    type,
    formikProps,
    style,
}) => {
    const [customCourseNumberActive, setCustomCourseNumberActive] = useState(
        formikProps.values.courseNumberFormat ===
            CourseNumberFormat.CustomPrefix,
    );

    const {data} = useGetCourseNumberFormatQuery();

    const courseNumberFormatFromSettings: CourseNumberFormat | undefined =
        data?.settings?.courseNumberFormat;

    useEffect(() => {
        if (
            formikProps.values.courseNumberFormat ===
            CourseNumberFormat.CustomPrefix
        ) {
            setCustomCourseNumberActive(true);
        }
    }, [formikProps.values.courseNumberFormat]);

    const title =
        modalTitle ?? type === undefined
            ? undefined
            : i18n.views.GeneratedNumberBadge[type].title();

    const content =
        modalContent ?? type === undefined
            ? undefined
            : i18n.views.GeneratedNumberBadge[type].content();

    const handleChange = (e: boolean) => {
        setCustomCourseNumberActive(e);

        const newFormat = e
            ? CourseNumberFormat.CustomPrefix
            : courseNumberFormatFromSettings ?? CourseNumberFormat.NoPrefix;

        formikProps.setFieldValue("courseNumberFormat", newFormat);
    };

    const courseNumberFormatString = (
        courseNumberFormat?: CourseNumberFormat,
    ) => {
        switch (courseNumberFormat) {
            case CourseNumberFormat.CourseLocationPrefix:
                // console.log(
                //     i18n.containers.courses.Courses.courseNumberFormats.courseLocationPrefix(),
                // );

                return i18n.containers.courses.Courses.courseNumberFormats.courseLocationPrefix();

            case CourseNumberFormat.CourseTypePrefix:
                // console.log(
                //     i18n.containers.courses.Courses.courseNumberFormats.courseTypePrefix(),
                // );

                return i18n.containers.courses.Courses.courseNumberFormats.courseTypePrefix();

            case CourseNumberFormat.NoPrefix:
                // console.log(
                //     i18n.containers.courses.Courses.courseNumberFormats.noPrefix(),
                // );

                return i18n.containers.courses.Courses.courseNumberFormats.noPrefix();

            case CourseNumberFormat.CustomPrefix:
                // console.log(
                //     i18n.containers.courses.Courses.courseNumberFormats.customPrefix(),
                // );

                return i18n.containers.courses.Courses.courseNumberFormats.customPrefix();

            default:
                return "nichts gefunden";
                break;
        }
    };

    const labelWithSwitch = (
        <Space style={{display: "flex", alignItems: "center"}}>
            <div>
                {i18n.containers.courses.CourseCreate.courseNumber.label()}
            </div>
            <Switch
                unCheckedChildren="Automatisch generiert"
                checkedChildren="Manuell"
                checked={customCourseNumberActive}
                onChange={(e) => handleChange(e)}
            />
        </Space>
    );

    return (
        <Form.Item
            label={labelWithSwitch}
            style={style}
            className={css.courseNumberInput}
        >
            <span className={css.root}>
                {customCourseNumberActive ? (
                    <CourseNumberInput
                        fieldName="customCourseNumberPrefix"
                        label={i18n.containers.courses.CourseCreate.customCourseNumberPrefix.label()}
                        placeholder={i18n.containers.courses.CourseCreate.customCourseNumberPrefix.placeholder()}
                        formikProps={formikProps}
                        testId="CourseCourseNumberInput"
                        isActive={customCourseNumberActive}
                    />
                ) : (
                    <>
                        <InfoCircleTwoTone
                            onClick={() => openModal(title, content)}
                        />

                        <Tag color={colorPrimaryBackground}>
                            {courseNumberFormatString(
                                formikProps.values.courseNumberFormat,
                            )}
                        </Tag>
                    </>
                )}
            </span>
        </Form.Item>
    );
};

export default memo(GeneratedNumberBadge);
